import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { getValidTimezone } from "../../utils/dateFormatter";
import { METHANOL_STATUS_CONFIG } from "../../utils/methanolStatusConfig";
import MethanolStackedBarChart from "./MethanolStackedBarChart";
// ── Helpers ───────────────────────────────────────────────────
const formatDateMMDD = (date) => {
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${month}/${day}`;
};
const formatTooltipDate = (date, timezone) => {
    const safeTimezone = getValidTimezone(timezone) ?? "UTC";
    const monthName = date.toLocaleString("en-US", { month: "long", timeZone: safeTimezone });
    const day = date.toLocaleString("en-US", { day: "numeric", timeZone: safeTimezone });
    return `${day} ${monthName}`;
};
// ── Component ─────────────────────────────────────────────────
const MethanolHoursBarChart = ({ fromDate, statDetails, timezone = "UTC", toDate }) => {
    if (!statDetails.length) {
        return (_jsx("div", { className: "flex justify-center items-center h-[245px]", "data-test": "methanol-hours-no-data", children: "No data available" }));
    }
    // Collect unique canonical status keys — normalise casing and whitespace
    const allStatusKeys = [];
    statDetails.forEach((d) => {
        (d.runTime ?? []).forEach((rt) => {
            const key = rt.assetStatus.toLowerCase().replace(/\s+/g, "");
            if (!allStatusKeys.includes(key))
                allStatusKeys.push(key);
        });
    });
    // Build series using shared STATUS_CONFIG labels/colors
    const series = allStatusKeys.map((key) => ({
        color: METHANOL_STATUS_CONFIG[key]?.color ?? "#808080",
        label: METHANOL_STATUS_CONFIG[key]?.label ?? key.charAt(0).toUpperCase() + key.slice(1)
    }));
    // Build chart data — sum durations per status per day
    const dataByDate = {};
    statDetails.forEach((d) => {
        const dateKey = d.startTimestamp.substring(0, 10);
        const date = new Date(dateKey + "T00:00:00Z");
        const durationByStatus = {};
        (d.runTime ?? []).forEach((rt) => {
            const key = rt.assetStatus.toLowerCase().replace(/\s+/g, "");
            durationByStatus[key] = (durationByStatus[key] ?? 0) + rt.duration;
        });
        // Track which series indices were explicitly sent by the backend for this day
        const presentIndices = allStatusKeys
            .map((key, i) => (key in durationByStatus ? i : -1))
            .filter((i) => i !== -1);
        dataByDate[dateKey] = {
            date: formatDateMMDD(date),
            presentIndices,
            tooltipDate: formatTooltipDate(date, timezone),
            values: allStatusKeys.map((key) => durationByStatus[key] ?? 0)
        };
    });
    // Fill missing dates in selected range with all-zero bars
    const startDateStr = fromDate
        ? fromDate.substring(0, 10)
        : (Object.keys(dataByDate).sort((a, b) => a.localeCompare(b))[0] ?? "");
    const endDateStr = toDate
        ? toDate.substring(0, 10)
        : (Object.keys(dataByDate)
            .sort((a, b) => a.localeCompare(b))
            .at(-1) ?? "");
    const chartData = [];
    if (startDateStr && endDateStr) {
        const current = new Date(startDateStr + "T00:00:00Z");
        const end = new Date(endDateStr + "T00:00:00Z");
        while (current <= end) {
            const key = current.toISOString().split("T")[0];
            if (dataByDate[key]) {
                chartData.push(dataByDate[key]);
            }
            else {
                chartData.push({
                    date: formatDateMMDD(current),
                    isNA: true,
                    tooltipDate: formatTooltipDate(current, timezone),
                    values: allStatusKeys.map(() => 0)
                });
            }
            current.setUTCDate(current.getUTCDate() + 1);
        }
    }
    return (_jsx("div", { "data-test": "methanol-hours-bar-chart-container", children: _jsx(MethanolStackedBarChart, { data: chartData, series: series, testId: "methanol-hours-bar-chart", tooltipUnit: "hours", unit: "h", xAxisLabel: "Days", yAxisMax: 24, yAxisStepSize: 4 }) }));
};
export default MethanolHoursBarChart;
